import type {TokenClaimsSerializer} from "@/generated/DjangoTypes";
import NewFeatureSection from "@/pages/registerCompany/NewFeatureSection";
import useTokenStore from "@/store/token";
import useUserStore from "@/store/user";
import {fetchMobileAppRedirect, fetchUser} from "@/utils/loaderUtils";
import {createFileRoute, Outlet, redirect, useLocation, useNavigate} from "@tanstack/react-router";
import {jwtDecode} from "jwt-decode";
import {useEffect} from "react";
import {useMedia} from "react-use";

const ROUTES_WITHOUT_SPLIT_LAYOUT = ["/onboarding/", "/verify-email/"] as const;

// Exempt so the AI crawl flow runs while authenticated (finish-setup modal / instant Google signup) instead of being redirected away mid-crawl.
const AUTH_REDIRECT_EXEMPT_PATHS = ["/onboarding"] as const;

const isAuthRedirectExempt = (pathname: string) => {
  const normalized = pathname.replace(/\/$/, "");
  return AUTH_REDIRECT_EXEMPT_PATHS.some(path => normalized === path || normalized.startsWith(`${path}/`));
};

export const Route = createFileRoute("/_unauthenticated")({
  beforeLoad: async ({location}) => {
    const token = useTokenStore.getState().access;

    if (!token || isAuthRedirectExempt(location.pathname)) {
      return;
    }

    const claims = jwtDecode<TokenClaimsSerializer>(token);
    const agencyId = claims.agency_ids?.[0];
    const isAgency = claims.access_level === "AGENCY";
    const isCompany = claims.access_level === "SINGLE_BRAND";
    const isInfluencer = claims.access_level === "INFLUENCER";

    if (isInfluencer) {
      const redirectData = await fetchMobileAppRedirect();
      return window.open(redirectData.redirect, "_self");
    }

    if (isAgency) {
      throw redirect({to: "/agency/$agencyId/dashboard", params: {agencyId: agencyId!}, replace: true});
    }

    if (isCompany) {
      const user = await fetchUser(claims.user_id);
      const company = user.sub_brands?.[0]?.company;
      const hasCampaign = company.has_campaign;
      const hasPlan = !!user.package_subscription;

      if (!hasPlan || !hasCampaign) {
        throw redirect({to: "/welcome", replace: true});
      }

      throw redirect({
        to: "/company/$companyId/dashboard",
        params: {companyId: company.id!},
        replace: true,
      });
    }

    throw redirect({to: "/campaigns", replace: true});
  },
  component: Unauthenticated,
});

function Unauthenticated() {
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const shouldShowNewFeatureSection = useMedia("(min-width: 1024px)");
  const userId = useUserStore(state => state.user.id);

  useEffect(() => {
    if (!!userId && !isAuthRedirectExempt(pathname)) {
      navigate({to: "/", replace: true});
    }
  }, [navigate, userId]);

  if (ROUTES_WITHOUT_SPLIT_LAYOUT.some(path => new RegExp(`^/?${path.replace(/^\/|\/$/g, "")}/?$`).test(pathname))) {
    return <Outlet />;
  }

  if (shouldShowNewFeatureSection) {
    return (
      <div className="hidden min-h-screen lg:grid lg:grid-cols-5">
        <div className="col-span-3 flex h-full flex-col items-center justify-between gap-10 overflow-y-auto pt-10 pb-7">
          <Outlet />
        </div>
        <NewFeatureSection />
      </div>
    );
  }

  return (
    <div className="max-w-wide container mx-auto flex flex-col items-center gap-10 pt-10 pb-20 lg:hidden">
      <Outlet />
    </div>
  );
}
